import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

import {
  GET_CALORIE_LOGS,
  GET_DIET_PLAN,
  GET_ME,
  GET_WORKOUTS,
  GET_WORKOUT_LOGS_STATS,
} from '../graphql/queries';
import { trackPageView, trackEvent } from '../utils/gt';
import {
  computeStreakDays,
  estimateWorkoutMinutes,
  getNextWorkout,
  getPreviewExercises,
  getRotation,
  getWeekDays,
  getWeekSummary,
} from '../utils/workoutStats';
import { isProAccount, lockedWorkoutIds } from '../utils/workoutLock';
import { useAppOnPhone } from '../utils/useAppOnPhone';

import { AppHomeView } from '../components/dashboard/AppHomeView';
import { DashboardView } from '../components/dashboard/DashboardView';
import { WelcomeTour } from '../components/welcome/WelcomeTour';

const DAY_MS = 24 * 60 * 60 * 1000;
// Meus treinos on Início lists the next few in the order they're trained; the whole plan is on Treinos
const LISTED_WORKOUTS = 5;

const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const Dashboard = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const appOnPhone = useAppOnPhone();
  const isPt = t('lang') === 'pt';
  const formats = useMemo(() => {
    const locale = isPt ? 'pt-BR' : 'en-US';
    return {
      number: new Intl.NumberFormat(locale),
      longDate: new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }),
      weekday: new Intl.DateTimeFormat(locale, { weekday: 'long' }),
      weekdayShort: new Intl.DateTimeFormat(locale, { weekday: 'short' }),
      weekdayLetter: new Intl.DateTimeFormat(locale, { weekday: 'narrow' }),
      monthDay: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
      dayMonth: new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }),
    };
  }, [isPt]);

  const { data: dataMe, loading: isLoadingMe } = useQuery(GET_ME);
  const { data: dataWorkouts, loading: isLoadingWorkouts } = useQuery(GET_WORKOUTS);
  const { data: dataLogs, loading: isLoadingLogs } = useQuery(GET_WORKOUT_LOGS_STATS);
  const { data: dataDiet, loading: isLoadingDiet } = useQuery(GET_DIET_PLAN);
  const today = todayIso();
  const { data: dataCalories } = useQuery(GET_CALORIE_LOGS, { variables: { dateGte: today, dateLte: today } });

  const me = dataMe?.me;
  const hasWelcomeParam = !!searchParams.get('w');

  useEffect(() => {
    trackPageView('dashboard', `${t('appUrl')}/dashboard`, '/dashboard');
  }, [t]);

  const view = useMemo(() => {
    const home = (key, options) => t(`pages.Dashboard.home.${key}`, options);
    const now = new Date();
    const formatNumber = (value) => formats.number.format(Math.round(value));
    // "quarta-feira" reads "quarta" in a short line, as people write it
    const weekdayName = (date) => formats.weekday.format(date).replace(/-feira$/, '');

    // Workouts kept locked after a Pro subscription ended stay on Meus Treinos only
    const allWorkouts = dataWorkouts?.workouts?.data || [];
    const locked = lockedWorkoutIds(allWorkouts, dataMe?.me);
    const workouts = allWorkouts.filter((workout) => !locked.has(workout.id));
    const logs = dataLogs?.workoutLogs?.data || [];
    const prefix = home('workoutPrefix');
    const title = (workout) => `${prefix} ${workout?.attributes?.name || ''}`.trim();
    const titleOfLog = (log) => title(allWorkouts.find((workout) => workout.id === log.attributes?.workout?.data?.id));

    const next = getNextWorkout(workouts, logs);
    const nextExercises = next?.attributes?.exercises || [];
    const week = getWeekSummary(logs, workouts.length, now);
    // Volume adds up in kg (sets logged in lbs are converted), as Progresso shows it, in both languages
    const volumeUnit = 'kg';

    // When a workout was last done: "hoje", "ontem", "sábado" within the week, "24/10" before that
    const lastDone = (workout) => {
      const completedAt = logs.find((log) => log.attributes?.workout?.data?.id === workout.id)?.attributes?.completedAt;
      if (!completedAt) return null;
      const date = new Date(completedAt);
      const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
      if (daysAgo <= 0) return home('lastToday');
      if (daysAgo === 1) return home('lastYesterday');
      if (daysAgo < 7) return weekdayName(date);
      return formats.dayMonth.format(date);
    };

    // The week by day for the chart: a bar per day, as tall as its volume against the week's biggest day
    const weekDays = getWeekDays(logs, now);
    const topVolume = Math.max(...weekDays.map((day) => day.volume), 0);
    const days = weekDays.map((day, index) => {
      const isDone = day.logs.length > 0;
      const minutes = Math.round(day.duration / 60);
      const volume = day.volume > 0 ? `${formatNumber(day.volume)} ${volumeUnit}` : null;
      let summary = home('dayRest');
      if (isDone) {
        summary = [day.logs.map(titleOfLog).join(' + '), minutes > 0 && `${minutes} ${home('minutesUnit')}`, volume]
          .filter(Boolean)
          .join(' · ');
      } else if (day.isToday && next) {
        summary = home('dayTodo', { title: title(next) });
      } else if (day.isToday || day.isFuture) {
        summary = home('dayAhead');
      }
      return {
        key: index,
        short: capitalize(formats.weekdayShort.format(day.date).replace(/\.$/, '')),
        letter: formats.weekdayLetter.format(day.date),
        name: home('dayName', {
          day: day.isToday ? home('today') : capitalize(weekdayName(day.date)),
          date: isPt ? day.date.getDate() : formats.monthDay.format(day.date),
        }),
        isToday: day.isToday,
        isDone,
        // A bodyweight session has no volume: its bar still stands, at full height when no day has any
        height: isDone ? Math.max(10, topVolume > 0 ? (day.volume / topVolume) * 100 : 100) : day.isToday ? 100 : 0,
        volume,
        summary,
      };
    });
    // The chart opens on the last day trained this week, or on today before the first session
    const lastTrained = days.reduce((found, day, index) => (day.isDone ? index : found), -1);
    const todayIndex = days.findIndex((day) => day.isToday);

    const minutesTrained = Math.round(week.duration / 60);
    const hours = Math.floor(minutesTrained / 60);

    const dietTarget = dataDiet?.dietPlan?.data?.attributes?.nutritionData?.targetCalories;
    const eaten = dataCalories?.calorieLogs?.data?.[0]?.attributes?.totalCalories || 0;
    const left = dietTarget ? Math.round(dietTarget - eaten) : 0;

    return {
      dateLine: capitalize(formats.longDate.format(now)),
      today: next && {
        id: next.id,
        title: title(next),
        muscles: next.attributes?.description,
        exerciseCount: nextExercises.length,
        setCount: nextExercises.reduce((total, exercise) => total + (parseInt(exercise.series) || 0), 0),
        minutes: estimateWorkoutMinutes(nextExercises),
        thumbnails: getPreviewExercises(nextExercises.filter((exercise) => exercise.thumbnailUrl))
          .slice(0, 4)
          .map((exercise, index) => ({ key: `${index}-${exercise.name}`, src: exercise.thumbnailUrl, alt: exercise.name })),
      },
      week: {
        sessions: week.sessions,
        target: week.target,
        progress: week.target ? Math.min(week.sessions / week.target, 1) : 0,
      },
      streak: computeStreakDays(logs, now),
      volume: { value: formatNumber(week.volume), unit: volumeUnit },
      time: [
        hours > 0 && { value: hours, unit: home('hoursUnit') },
        (hours === 0 || minutesTrained % 60 > 0) && { value: minutesTrained % 60, unit: home('minutesUnit') },
      ].filter(Boolean),
      days,
      selectedDay: lastTrained >= 0 ? lastTrained : Math.max(todayIndex, 0),
      workouts: getRotation(workouts, next)
        .slice(0, LISTED_WORKOUTS)
        .map((workout) => ({
          id: workout.id,
          letter: workout.attributes?.name,
          title: title(workout),
          muscles: workout.attributes?.description,
          lastDone: lastDone(workout),
          isNext: workout.id === next?.id,
        })),
      diet: dietTarget
        ? {
            eaten: formatNumber(eaten),
            target: formatNumber(dietTarget),
            progress: eaten / dietTarget,
            percent: Math.round((eaten / dietTarget) * 100),
            status: left > 0 ? 'left' : left < 0 ? 'over' : 'done',
            left: formatNumber(Math.abs(left)),
          }
        : null,
    };
  }, [dataWorkouts, dataMe, dataLogs, dataDiet, dataCalories, formats, isPt, t]);

  const shared = {
    greetingName: (me?.name || '').trim().split(/\s+/)[0],
    ...view,
    // Skeletons until the data answers: the "create your first workout" card only once the API says there's none.
    // Today's workout waits for the logs too, which decide which one is next.
    loading: {
      name: isLoadingMe && !dataMe,
      today: (isLoadingWorkouts && !dataWorkouts) || (isLoadingLogs && !dataLogs),
      diet: isLoadingDiet && !dataDiet,
    },
    onStart: () => {
      trackEvent('dashboard_start_workout', 'Dashboard - Start Guided Workout', 'dashboard', true);
      // Straight to the guided workout: its opening offers the Alexa too (and sends this page's choice events)
      navigate(`/execute/${view.today.id}`, { state: { from: 'dashboard' } });
    },
    onOpenToday: () => trackEvent('dashboard_open_today', 'Dashboard - Open Today Workout', 'dashboard', true),
    onNavigate: (key) => trackEvent(`dashboard_${key}`, `Dashboard - ${key}`, 'dashboard', true),
    onOpenPartner: () => trackEvent('dashboard_partner_qualwheycomprar', 'Dashboard - Partner Qual Whey Comprar', 'dashboard', true),
  };

  return (
    <div className="mt-root mt-root--app">
      {appOnPhone ? (
        // Pro-only rows lock once the account is known to be free, never while it loads
        <AppHomeView {...shared} isFree={!!me && !isProAccount(me)} />
      ) : (
        <DashboardView
          {...shared}
          onOpenWorkout={() => trackEvent('dashboard_open_workout', 'Dashboard - Open Workout', 'dashboard', true)}
          onSelectDay={() => trackEvent('dashboard_week_day', 'Dashboard - Week Day', 'dashboard', true)}
        />
      )}
      {hasWelcomeParam && (
        <WelcomeTour
          firstName={me?.name?.trim().split(/\s+/)[0]}
          onDone={() => setSearchParams('')}
          onCreateWorkout={() => navigate('/workout')}
        />
      )}
    </div>
  );
};
